import _ from 'lodash';
import { useState } from 'react';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';

import { URLS } from 'config/urls';
import { useAuth } from 'entities/Auth';
import AuthPage from 'components/AuthPage';
import Input from 'components/Input';
import Button from 'components/Button';
import LanguageSelector from 'components/LanguageSelector';
import { PasswordInput } from 'components/PasswordInput';

const schema = yup.object().shape({
  email: yup
    .string()
    .email(() => t('common.invalidEmailLabel'))
    .required(() => t('common.emailIsRequiredLabel')),
  password: yup.string().required(() => t('common.passwordIsRequiredLabel')),
});

interface ISetPasswordForm {
  email: string;
  password: string;
}

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [formErrors, setErrors] = useState({ errors: {}, message: '' });

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    control,
    reset,
  } = useForm<ISetPasswordForm>({
    defaultValues: { email: '', password: '' },
    mode: 'onBlur',
    resolver: yupResolver(schema),
  });

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const onSubmit: SubmitHandler<ISetPasswordForm> = async ({ email, password }) => {
    try {
      await login({ email, password });
    } catch (error: any) {
      reset();

      error.response?.text()?.then((data: string) => setErrors(JSON.parse(data)));
    }
  };

  return (
    <AuthPage>
      <div className='flex flex-col justify-center w-full'>
        <div className='w-full flex justify-between items-center pb-[24px]'>
          <h1 className='bold text-grey800 font-[DKLiquidEmbrace] text-3xl text-center md:text-left'>
            {t('login.title')}
          </h1>
          <LanguageSelector />
        </div>

        {!_.isEmpty(formErrors.errors) && (
          <div className='text-danger pb-[10px]'> {_.map(formErrors?.errors, (value: string) => value)}</div>
        )}
        {_.isEmpty(formErrors.errors) && formErrors.message !== '' && (
          <div className='text-danger pb-[10px]'>{formErrors.message}</div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className={'flex flex-col gap-[24px] max-sm:gap-[20px]'}>
          <Controller
            control={control}
            name='email'
            render={({ field, fieldState }) => (
              <Input
                type='email'
                label={t('login.emailLabel')}
                placeholder={t('login.emailPlaceholder')}
                error={fieldState.error}
                isTouched={fieldState.isTouched}
                {...field}
              />
            )}
          />
          <Controller
            control={control}
            name='password'
            render={({ field, fieldState }) => (
              <PasswordInput
                label={t('login.passwordLabel')}
                placeholder={t('login.passwordPlaceholder')}
                error={fieldState.error}
                isTouched={fieldState.isTouched}
                {...field}
              />
            )}
          />
          <a
            href={URLS.FORGOT_PASSWORD}
            className='text-primary font-[MuseoSansCyrl] underline text-[14px] leading-[20px]'
          >
            {t('login.forgotPassword')}
          </a>
          <Button type='submit' className={'w-full md:w-fit font-semibold'} disabled={!isValid || isSubmitting}>
            {t('login.submitButton')}
          </Button>
        </form>
      </div>
    </AuthPage>
  );
};

export default Login;
